import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PaymentProcess from '@/components/PaymentProcess';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface PaymentStepProps {
  event: any;
  registrationData: any;
  onNext: () => void;
  onPrevious: () => void;
}

export const PaymentStep = ({ event, registrationData, onNext, onPrevious }: PaymentStepProps) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePaymentSuccess = () => {
    setIsProcessing(false);
    onNext();
  };

  const handlePaymentError = (error: string) => {
    setIsProcessing(false);
    console.error('Payment error:', error);
    // Handle payment error - could show a toast or error message
  };

  // Always calculate full amount - removed individual payment option
  const calculatePaymentAmount = () => {
    const baseAmount = event.entry_fee || 0;
    
    // For doubles, always pay for both players
    if (registrationData.modality === 'dobles') {
      return baseAmount * 2;
    }
    
    // For singles, just base amount
    return baseAmount;
  };

  const paymentData = {
    eventId: event.id,
    mode: (registrationData.modality === 'singles' ? 'individual' : 'parejas') as 'individual' | 'parejas',
    partner: (registrationData.partnerSelectionMethod === 'friend' || registrationData.partnerSelectionMethod === 'invitation')
      ? registrationData.selectedFriend?.full_name || registrationData.selectedFriend?.email
      : registrationData.player2Name || null,
    paymentMode: 'full_amount' as 'my_part' | 'full_amount', // Always pay full amount
    amount: calculatePaymentAmount()
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Información de Pago</h2>
        <p className="text-gray-600">
          Completa el pago para finalizar tu inscripción
        </p>
      </div>

      {/* Registration Summary */}
      <Card className="bg-gray-50 border-gray-200">
        <CardHeader>
          <CardTitle className="text-lg">Resumen de Inscripción</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Evento:</span>
            <span className="font-medium">{event.title}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Modalidad:</span>
            <Badge variant="outline">
              {registrationData.modality === 'singles' ? 'Singles' : 'Dobles'}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Categoría:</span>
            <span className="font-medium">{registrationData.category}</span>
          </div>
          {registrationData.subcategory && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Subcategoría:</span>
              <span className="font-medium">{registrationData.subcategory}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Jugador 1:</span>
            <span className="font-medium">{registrationData.player1Name}</span>
          </div>
          
          {registrationData.modality === 'dobles' && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Compañero:</span>
                <span className="font-medium">
                  {(registrationData.partnerSelectionMethod === 'friend' || registrationData.partnerSelectionMethod === 'invitation')
                    ? registrationData.selectedFriend?.full_name || registrationData.selectedFriend?.email
                    : registrationData.player2Name}
                </span>
              </div>
              
              {(registrationData.partnerSelectionMethod === 'friend' || registrationData.partnerSelectionMethod === 'invitation') && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Tipo de pago:</span>
                  <Badge variant="default">
                    Pago completo
                  </Badge>
                </div>
              )}
            </>
          )}
          
          <div className="border-t pt-3 mt-3">
            <div className="flex justify-between items-center text-lg font-semibold">
              <span>Total a pagar:</span>
              <span className="text-green-600">${calculatePaymentAmount()} MXN</span>
            </div>
            {registrationData.modality === 'dobles' && registrationData.partnerSelectionMethod === 'friend' && (
              <p className="text-xs text-gray-500 mt-1">
                Incluye inscripción completa para ambos jugadores. Se enviará invitación a tu compañero.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment Component */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Método de Pago
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PaymentProcess
            paymentData={paymentData}
            eventTitle={event.title}
            eventData={event}
            registrationData={registrationData}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            onBack={onPrevious}
          />
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={onPrevious}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver a Datos
        </Button>
      </div>
    </div>
  );
};